import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Grid,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

/**
 * Check if a field should be shown according to its showWhen definition
 */
export const shouldShowField = (field, scopeValues = {}, rootValues = {}) => {
  if (!field || !field.showWhen) return true;

  for (const [depKey, expected] of Object.entries(field.showWhen)) {
    let actual = scopeValues?.[depKey];
    if (actual === undefined && rootValues?.[depKey] !== undefined) {
      actual = rootValues[depKey];
    }

    if (Array.isArray(expected)) {
      if (!expected.includes(actual)) {
        return false;
      }
    } else {
      if (actual !== expected) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Resolve options based on static options or optionsByField
 */
export const resolveFieldOptions = (field, scopeValues = {}, rootValues = {}) => {
  if (!field) return [];

  if (field.optionsByField) {
    const depKey = field.optionsByField.field;
    let depVal = scopeValues?.[depKey];
    if (depVal === undefined && rootValues?.[depKey] !== undefined) {
      depVal = rootValues[depKey];
    }

    if (depVal && field.optionsByField.options?.[depVal]) {
      return field.optionsByField.options[depVal];
    }
    return [];
  }

  return field.options || [];
};

/**
 * Flatten tree options with breadcrumbs (e.g., "Assisted › Donor › Male")
 */
export const flattenTreeOptions = (options = [], prefix = '', depth = 0) => {
  let flat = [];
  if (!Array.isArray(options)) return flat;

  options.forEach((opt) => {
    const currentLabel = prefix ? `${prefix} › ${opt.label || opt.value}` : (opt.label || opt.value);
    const hasChildren = Array.isArray(opt.children) && opt.children.length > 0;

    flat.push({
      value: opt.value,
      label: currentLabel,
      shortLabel: opt.label || opt.value,
      depth,
      isParent: hasChildren,
    });

    if (hasChildren) {
      flat = flat.concat(flattenTreeOptions(opt.children, currentLabel, depth + 1));
    }
  });

  return flat;
};

/**
 * Recursively builds the chosen option object down to the last level:
 * { value, label, children: [chosen child] }
 */
export const buildTreeOptionObject = (options = [], targetValue) => {
  if (!targetValue || !Array.isArray(options)) return null;

  for (const opt of options) {
    if (opt.value === targetValue) {
      return { value: opt.value, label: opt.label || opt.value };
    }
    if (Array.isArray(opt.children) && opt.children.length > 0) {
      const childMatch = buildTreeOptionObject(opt.children, targetValue);
      if (childMatch) {
        return {
          value: opt.value,
          label: opt.label || opt.value,
          children: [childMatch],
        };
      }
    }
  }
  return null;
};

/**
 * Extracts leaf value string from a tree value (string or nested object)
 */
export const extractLeafValueFromTree = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    let curr = val;
    while (Array.isArray(curr.children) && curr.children.length > 0) {
      curr = curr.children[0];
    }
    return curr?.value || '';
  }
  return '';
};

/**
 * Extracts an array of string values from a multitree value (array of objects or strings)
 */
export const extractValuesFromMultitree = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val.map((item) => (typeof item === 'object' ? extractLeafValueFromTree(item) : item)).filter(Boolean);
  }
  return [];
};

/**
 * Generate a blank default item for a list based on its subfields
 */
export const createDefaultListItem = (subFields = []) => {
  const item = {};
  subFields.forEach((f) => {
    if (f.defaultValue !== undefined) {
      item[f.key] = f.defaultValue;
    } else if (f.type === 'list' || f.type === 'multiselect' || f.type === 'multitree') {
      item[f.key] = [];
    } else if (f.type === 'boolean') {
      item[f.key] = false;
    } else if (f.type === 'group' && Array.isArray(f.fields)) {
      item[f.key] = createDefaultListItem(f.fields);
    } else {
      item[f.key] = null;
    }
  });
  return item;
};

/**
 * Calculate appropriate grid column span for a field
 */
export const getFieldGridColumns = (field) => {
  if (field.type === 'textarea' || field.type === 'list' || field.type === 'group') {
    return { xs: 12, sm: 12, md: 12 };
  }
  if (field.type === 'boolean') {
    return { xs: 12, sm: 6, md: 4 };
  }
  if (field.type === 'number' || field.type === 'date') {
    return { xs: 12, sm: 6, md: 4 };
  }
  if (field.type === 'multiselect' || field.type === 'multitree') {
    return { xs: 12, sm: 12, md: 6 };
  }
  return { xs: 12, sm: 6, md: 6 };
};

const DynamicFieldRenderer = ({
  field,
  value,
  onChange,
  scopeValues = {},
  rootValues = {},
  disabled = false,
  cardType = '',
}) => {
  if (!field) return null;

  // Check showWhen visibility
  if (!shouldShowField(field, scopeValues, rootValues)) {
    return null;
  }

  // Hidden type
  if (field.type === 'hidden') {
    return null;
  }

  const isFieldDisabled = disabled || field.readOnly === true;
  const rawOptions = resolveFieldOptions(field, scopeValues, rootValues);

  // NUMBER INPUT
  if (field.type === 'number') {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={field.label}
        value={value !== null && value !== undefined ? value : ''}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === '' ? null : Number(val));
        }}
        disabled={isFieldDisabled}
        required={field.required}
        data-testid={`input-${field.key}`}
        slotProps={{
          input: {
            endAdornment: field.unit ? (
              <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 12, fontWeight: 600 } }}>
                {field.unit}
              </InputAdornment>
            ) : null,
            inputProps: {
              step: field.step ?? 'any',
              min: field.min,
              max: field.max,
            },
          },
        }}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      />
    );
  }

  // TEXT INPUT
  if (field.type === 'text') {
    return (
      <TextField
        fullWidth
        size="small"
        label={field.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isFieldDisabled}
        required={field.required}
        data-testid={`input-${field.key}`}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      />
    );
  }

  // TEXTAREA
  if (field.type === 'textarea') {
    return (
      <TextField
        fullWidth
        size="small"
        multiline
        rows={3}
        label={field.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isFieldDisabled}
        required={field.required}
        data-testid={`textarea-${field.key}`}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      />
    );
  }

  // DATE
  if (field.type === 'date') {
    const dateStr = value ? String(value).slice(0, 10) : '';
    return (
      <TextField
        fullWidth
        size="small"
        type="date"
        label={field.label}
        value={dateStr}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={isFieldDisabled}
        required={field.required}
        data-testid={`date-${field.key}`}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      />
    );
  }

  // BOOLEAN (Yes / No Toggle)
  if (field.type === 'boolean') {
    return (
      <Box
        sx={{
          p: 1.25,
          border: '1px solid #E2E8F0',
          borderRadius: 2,
          bgcolor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}
        data-testid={`boolean-${field.key}`}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
          {field.label} {field.required && <span style={{ color: '#EF4444' }}>*</span>}
        </Typography>

        <ToggleButtonGroup
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          exclusive
          onChange={(e, val) => {
            if (val === 'yes') onChange(true);
            else if (val === 'no') onChange(false);
            else onChange(null);
          }}
          size="small"
          disabled={isFieldDisabled}
          sx={{ height: 32 }}
        >
          <ToggleButton
            value="yes"
            sx={{
              px: 2,
              py: 0.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              '&.Mui-selected': {
                bgcolor: '#DCFCE7 !important',
                color: '#15803D !important',
                borderColor: '#86EFAC',
              },
            }}
          >
            Yes
          </ToggleButton>
          <ToggleButton
            value="no"
            sx={{
              px: 2,
              py: 0.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              '&.Mui-selected': {
                bgcolor: '#F1F5F9 !important',
                color: '#64748B !important',
                borderColor: '#CBD5E1',
              },
            }}
          >
            No
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  // SELECT (Dropdown)
  if (field.type === 'select') {
    return (
      <FormControl
        fullWidth
        size="small"
        disabled={isFieldDisabled}
        required={field.required}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      >
        <InputLabel id={`label-${field.key}`}>{field.label}</InputLabel>
        <Select
          labelId={`label-${field.key}`}
          label={field.label}
          value={value !== null && value !== undefined ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          data-testid={`select-${field.key}`}
        >
          <MenuItem value="">
            <em style={{ color: '#94A3B8' }}>Select {field.label}...</em>
          </MenuItem>
          {rawOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label || opt.value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // TREE (Cascading dropdown -> chosen option object down to last level)
  if (field.type === 'tree') {
    const flatTree = flattenTreeOptions(rawOptions);
    const selectedLeaf = extractLeafValueFromTree(value);

    return (
      <FormControl
        fullWidth
        size="small"
        disabled={isFieldDisabled}
        required={field.required}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      >
        <InputLabel id={`label-${field.key}`}>{field.label}</InputLabel>
        <Select
          labelId={`label-${field.key}`}
          label={field.label}
          value={selectedLeaf}
          onChange={(e) => {
            const chosenLeaf = e.target.value;
            if (!chosenLeaf) {
              onChange(null);
            } else {
              const treeObj = buildTreeOptionObject(rawOptions, chosenLeaf);
              onChange(treeObj || { value: chosenLeaf, label: chosenLeaf });
            }
          }}
          data-testid={`tree-${field.key}`}
        >
          <MenuItem value="">
            <em style={{ color: '#94A3B8' }}>Select {field.label}...</em>
          </MenuItem>
          {flatTree.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                fontWeight: opt.isParent ? 600 : 400,
                color: opt.isParent ? '#475569' : '#0F172A',
                pl: 2 + opt.depth * 1.5,
                fontSize: opt.depth > 0 ? 13 : 14,
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // MULTITREE (Multi-select of option objects)
  if (field.type === 'multitree') {
    const flatTree = flattenTreeOptions(rawOptions);
    const optionsList = flatTree.length > 0 ? flatTree : rawOptions;

    const currentLeafValues = extractValuesFromMultitree(value);
    const selectedOptions = optionsList.filter((opt) => currentLeafValues.includes(opt.value));

    return (
      <Autocomplete
        multiple
        size="small"
        disabled={isFieldDisabled}
        options={optionsList}
        getOptionLabel={(opt) => opt.label || opt.value || ''}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        value={selectedOptions}
        onChange={(event, newSelected) => {
          const optionObjects = newSelected.map((s) => {
            return buildTreeOptionObject(rawOptions, s.value) || { value: s.value, label: s.label || s.value };
          });
          onChange(optionObjects);
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.shortLabel || option.label || option.value}
              size="small"
              sx={{
                height: 24,
                fontSize: 11,
                bgcolor: '#F3E8FF',
                color: '#6D28D9',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            placeholder={currentLeafValues.length === 0 ? `Select ${field.label}...` : ''}
            required={field.required && currentLeafValues.length === 0}
            data-testid={`multitree-${field.key}`}
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
        )}
      />
    );
  }

  // MULTISELECT (Array of option value strings)
  if (field.type === 'multiselect') {
    const currentValues = Array.isArray(value) ? value : [];
    const selectedOptions = rawOptions.filter((opt) => currentValues.includes(opt.value));

    return (
      <Autocomplete
        multiple
        size="small"
        disabled={isFieldDisabled}
        options={rawOptions}
        getOptionLabel={(opt) => opt.label || opt.value || ''}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        value={selectedOptions}
        onChange={(event, newSelected) => {
          onChange(newSelected.map((s) => s.value));
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label || option.value}
              size="small"
              sx={{
                height: 24,
                fontSize: 11,
                bgcolor: '#F3E8FF',
                color: '#6D28D9',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            placeholder={currentValues.length === 0 ? `Select ${field.label}...` : ''}
            required={field.required && currentValues.length === 0}
            data-testid={`multiselect-${field.key}`}
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
        )}
      />
    );
  }

  // GROUP (Fieldset container with nested subfields)
  if (field.type === 'group') {
    const groupVal = value && typeof value === 'object' ? value : {};
    const subFields = Array.isArray(field.fields) ? field.fields : [];

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          bgcolor: '#F8FAFC',
          width: '100%',
        }}
        data-testid={`group-${field.key}`}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
          {field.label}
        </Typography>

        <Grid container spacing={2}>
          {subFields.map((subField) => {
            if (!shouldShowField(subField, groupVal, rootValues)) return null;
            const gridCols = getFieldGridColumns(subField);

            return (
              <Grid
                size={gridCols}
                key={subField.key}
              >
                <DynamicFieldRenderer
                  field={subField}
                  value={groupVal[subField.key]}
                  onChange={(subVal) => {
                    onChange({
                      ...groupVal,
                      [subField.key]: subVal,
                    });
                  }}
                  scopeValues={groupVal}
                  rootValues={rootValues}
                  disabled={isFieldDisabled}
                  cardType={cardType}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  }

  // LIST (Dynamic repeatable items with recursive subfield support)
  if (field.type === 'list') {
    const listItems = Array.isArray(value) ? value : [];
    const itemFields = Array.isArray(field.fields) ? field.fields : [];

    const handleAddItem = () => {
      const newItem = createDefaultListItem(itemFields);
      onChange([...listItems, newItem]);
    };

    const handleRemoveItem = (index) => {
      const updated = listItems.filter((_, i) => i !== index);
      onChange(updated);
    };

    const handleItemFieldChange = (index, subKey, subVal) => {
      const updated = [...listItems];
      updated[index] = {
        ...updated[index],
        [subKey]: subVal,
      };
      onChange(updated);
    };

    const minItems = field.minItems || 0;
    const canDelete = !isFieldDisabled && listItems.length > minItems;

    return (
      <Box sx={{ width: '100%', mt: 1, mb: 1 }} data-testid={`list-${field.key}`}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
            {field.label} ({listItems.length} {listItems.length === 1 ? 'entry' : 'entries'})
          </Typography>
        </Stack>

        {listItems.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              border: '1px dashed #CBD5E1',
              borderRadius: 2,
              bgcolor: '#F8FAFC',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
              No entries recorded in {field.label.toLowerCase()}.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {listItems.map((item, idx) => {
              // Construct title for item
              let itemTitle = `${field.label} #${idx + 1}`;
              if (item.historyType) {
                itemTitle = `${item.historyType} delivery${item.week ? ` - Week ${item.week}` : ''}${item.place ? `, ${item.place}` : ''}`;
              } else if (item.diseaseId) {
                // Find disease label
                const diseaseField = itemFields.find((f) => f.key === 'diseaseId');
                const diseaseOpt = diseaseField?.options?.find((o) => o.value === item.diseaseId);
                itemTitle = diseaseOpt?.label || `Condition #${idx + 1}`;
              }

              return (
                <Paper
                  key={item.id || idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#CBD5E1' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={`#${idx + 1}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: 11,
                          bgcolor: '#F1F5F9',
                          color: '#475569',
                        }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        {itemTitle}
                      </Typography>
                    </Stack>

                    {canDelete && (
                      <Tooltip title="Delete entry">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(idx)}
                          data-testid={`delete-item-${field.key}-${idx}`}
                          sx={{
                            color: '#94A3B8',
                            '&:hover': { color: '#EF4444', bgcolor: '#FEE2E2' },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Divider sx={{ mb: 2, borderColor: '#F1F5F9' }} />

                  <Grid container spacing={2}>
                    {itemFields.map((subField) => {
                      if (!shouldShowField(subField, item, rootValues)) return null;
                      const gridCols = getFieldGridColumns(subField);

                      return (
                        <Grid
                          size={gridCols}
                          key={subField.key}
                        >
                          <DynamicFieldRenderer
                            field={subField}
                            value={item[subField.key]}
                            onChange={(val) => handleItemFieldChange(idx, subField.key, val)}
                            scopeValues={item}
                            rootValues={rootValues}
                            disabled={isFieldDisabled}
                            cardType={cardType}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  }

  return null;
};

export default DynamicFieldRenderer;
